import type { ReactNode } from 'react';
import { Button, Typography } from '@mui/material';

import {
  type IntakeJourneyContent,
  IntakeJourneyShell,
  type IntakeJourneyStage,
  type IntakeJourneyStageState,
} from './IntakeJourneyShell';
import { IntakeJourneyStatus } from './IntakeJourneyStatus';
import { IntakeKickoffCard } from './IntakeKickoffCard';
import { IntakeVerificationStatus } from './IntakeVerificationStatus';

export interface IntakeJourneyProgress {
  /** Agreements & setup counts as done. Supplied by the agreement owner. */
  agreementsDone: boolean;
  agreementsCaption: string;
  /** Current provider's intake percent complete, from the live form. */
  intakePercent: number;
  /** True only when the current provider dashboard reports verified_at. */
  verificationDone?: boolean;
}

const LOCKED_AFTER_AGREEMENTS = 'Opens after agreements.';

/** The design's lock ladder, from the signals we actually have. Staff embeds pass no progress. */
export function journeyStagesFromProgress(
  progress: IntakeJourneyProgress,
  payerEnabled: boolean,
): Record<IntakeJourneyStage, IntakeJourneyStageState> {
  const intakeDone = progress.intakePercent >= 100;
  return {
    agreements: {
      statusLabel: progress.agreementsDone ? 'Services saved' : 'In progress',
      caption: progress.agreementsCaption,
      tone: 'sug',
    },
    intake: intakeDone
      ? { statusLabel: 'Complete', caption: 'Everything we needed is in', completed: true }
      : {
          statusLabel: 'In progress',
          caption: `${Math.round(progress.intakePercent)}% complete`,
          tone: 'sug',
        },
    verification: progress.verificationDone
      ? { statusLabel: 'Verified', caption: 'Your intake has been verified', completed: true }
      : !intakeDone
        ? { statusLabel: 'Locked', lockedReason: 'Opens when your intake is in.' }
        : { statusLabel: 'In review', caption: 'Usually one business day', tone: 'info' },
    payers: !payerEnabled
      ? { statusLabel: 'Unavailable', lockedReason: 'Payer connections are not enabled.' }
      : !progress.verificationDone
        ? { statusLabel: 'Locked', lockedReason: 'Opens after JotPsych verifies your intake.' }
        : {
            statusLabel: 'View current status',
            caption: 'Enrollment with each payer',
            tone: 'info',
          },
    import: !progress.agreementsDone
      ? { statusLabel: 'Locked', lockedReason: LOCKED_AFTER_AGREEMENTS }
      : { statusLabel: 'Open', caption: 'Start any time. Runs alongside', tone: 'info' },
  };
}

/**
 * Completion remains owned by each live feature. The owner must key this tree by
 * stable practice/auth identity on scope changes, preserving the key during refetch.
 */
export function LiveIntakeJourney({
  enabled = true,
  selectedStage,
  onNavigate,
  intake,
  payers,
  agreements,
  progress,
  payerEnabled,
  onKickoffClick,
  agreementContent,
  agreementStatusLabel,
}: {
  enabled?: boolean;
  selectedStage: IntakeJourneyStage;
  onNavigate: (stage: IntakeJourneyStage) => void;
  /** Return a stable form tree and pass effective activity through; do not conditionally mount it. */
  intake: IntakeJourneyContent;
  payers: ReactNode;
  /** Live Agreements & setup content. Omit for the read-only placeholder (staff embed). */
  agreements?: ReactNode;
  /** Live signals for the lock ladder. Omit to keep every stage open (staff preview). */
  progress?: IntakeJourneyProgress;
  payerEnabled: boolean;
  onKickoffClick?: () => void;
  agreementContent?: ReactNode;
  /** Caller-owned presentation, including confirmed completion; never inferred from SDK events. */
  agreementStatusLabel?: string;
}): React.JSX.Element {
  const stages = progress
    ? journeyStagesFromProgress(progress, payerEnabled)
    : {
        agreements: { statusLabel: agreements ? 'Available' : 'Coming soon' },
        intake: { statusLabel: 'Available' },
        verification: { statusLabel: 'Coming soon' },
        payers: payerEnabled
          ? { statusLabel: 'View current status' }
          : { statusLabel: 'Unavailable', lockedReason: 'Payer connections are not enabled.' },
        import: { statusLabel: 'Coming soon' },
      };
  if (agreementStatusLabel)
    stages.agreements = { ...stages.agreements, statusLabel: agreementStatusLabel };
  return (
    <IntakeJourneyShell
      showJourneyChrome={enabled}
      selectedStage={selectedStage}
      onNavigate={onNavigate}
      stages={stages}
      // The live setup rail carries its own kickoff step.
      kickoff={agreements ? undefined : <IntakeKickoffCard onScheduleClick={onKickoffClick} />}
      content={{
        agreements: agreements ?? agreementContent ?? (
          <IntakeJourneyStatus
            title="Your agreements will appear here"
            statusLabel="DocuSign signing coming soon"
            action={
              <Button variant="contained" onClick={() => onNavigate('intake')}>
                Continue to intake
              </Button>
            }
          >
            <Typography>
              You’ll be able to review and sign your practice agreements here when signing is ready.
              You can continue your intake now.
            </Typography>
          </IntakeJourneyStatus>
        ),
        intake,
        verification: progress ? (
          <IntakeVerificationStatus verified={progress.verificationDone} />
        ) : (
          <IntakeJourneyStatus
            title="Practice review status is coming soon"
            statusLabel="Not available yet"
            action={
              <Button variant="outlined" onClick={() => onNavigate('intake')}>
                Return to intake
              </Button>
            }
          >
            <Typography>
              Your existing intake and review tools remain available. This page does not yet show
              whether your practice has been verified.
            </Typography>
          </IntakeJourneyStatus>
        ),
        // Never instantiate a feature whose own access gate is disabled.
        payers: payerEnabled && (!progress || progress.verificationDone) ? payers : null,
        // Placeholder until the EHR import surface lands in the hub (GDN-3733).
        import: (
          <IntakeJourneyStatus
            title="Import patients"
            statusLabel="Coming soon"
            action={
              <Button variant="outlined" onClick={() => onNavigate('intake')}>
                Return to intake
              </Button>
            }
          >
            <Typography>
              Independent of verification and payers. Start it whenever your export is ready. Your
              first claim needs a patient with insurance on file, so this and payer connections both
              have to finish before you can bill.
            </Typography>
          </IntakeJourneyStatus>
        ),
      }}
    />
  );
}
